import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CountdownTimer } from "./CountdownTimer";
import { X } from "lucide-react";
import { Link } from "react-router-dom";

interface Promotion {
  id: string;
  name: string;
  description: string;
  discount_percentage: number;
  end_date: string;
  countdown_type: string;
  is_active: boolean;
}

interface PromotionCourse {
  course_id: string;
  courses: {
    id: string;
    slug: string;
    title: string;
  };
}

export function PromotionPopup() {
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [courses, setCourses] = useState<PromotionCourse[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadActivePromotion();

    // Subscribe to realtime promotion changes
    const channel = supabase
      .channel('promotions-popup')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'promotions',
          filter: 'show_popup=eq.true'
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const promo = payload.new as Promotion;
            if (promo.is_active) {
              loadActivePromotion();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadActivePromotion = async () => {
    // Check if user has already seen the popup today
    const lastSeen = localStorage.getItem("promotionPopupLastSeen");
    const today = new Date().toDateString();
    
    if (lastSeen === today) {
      return;
    }

    const { data: promotions } = await supabase
      .from("promotions")
      .select("*")
      .eq("is_active", true)
      .eq("show_popup", true)
      .gte("end_date", new Date().toISOString())
      .lte("start_date", new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1);

    if (promotions && promotions.length > 0) {
      const promo = promotions[0];
      setPromotion(promo);

      // Load associated courses
      const { data: promoCourses } = await supabase
        .from("promotion_courses")
        .select(`
          course_id,
          courses (id, slug, title)
        `)
        .eq("promotion_id", promo.id);

      if (promoCourses) {
        setCourses(promoCourses as PromotionCourse[]);
      }

      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("promotionPopupLastSeen", new Date().toDateString());
  };

  if (!promotion) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold">{promotion.name}</DialogTitle>
          <DialogDescription className="text-lg">{promotion.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl font-bold text-primary mb-2">
                {promotion.discount_percentage}%
              </div>
              <p className="text-xl">OFF</p>
            </div>
          </div>

          <div className="bg-muted p-6 rounded-lg">
            <p className="text-center text-sm font-medium mb-4">Time Remaining:</p>
            <CountdownTimer 
              endDate={promotion.end_date} 
              countdownType={promotion.countdown_type as "days" | "hours"}
            />
          </div>

          {courses.length > 0 && (
            <div>
              <p className="font-medium mb-2">Available for:</p>
              <div className="space-y-2">
                {courses.map((course) => (
                  <Link
                    key={course.course_id}
                    to={`/courses/${course.courses?.slug || course.course_id}`}
                    onClick={handleClose}
                  >
                    <Button variant="outline" className="w-full justify-start">
                      {course.courses.title}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button asChild className="flex-1">
              <Link to="/courses" onClick={handleClose}>
                View All Courses
              </Link>
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
